import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const KYCStatusPanel = ({ kycStatus }) => {
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10 border-success/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'rejected':
        return 'text-error bg-error/10 border-error/20';
      case 'not_started':
        return 'text-muted-foreground bg-muted border-border';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'rejected':
        return 'XCircle';
      case 'not_started':
        return 'Circle';
      default:
        return 'Circle';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Verified';
      case 'pending':
        return 'Under Review';
      case 'rejected':
        return 'Rejected';
      case 'not_started':
        return 'Not Started';
      default:
        return 'Unknown';
    }
  };

  const verificationSteps = [
    {
      id: 'identity',
      title: 'Identity Verification',
      description: 'Government-issued ID document',
      status: kycStatus?.identity || 'completed',
      required: true
    },
    {
      id: 'address',
      title: 'Address Verification',
      description: 'Proof of address document',
      status: kycStatus?.address || 'completed',
      required: true
    },
    {
      id: 'selfie',
      title: 'Selfie Verification',
      description: 'Photo verification for identity matching',
      status: kycStatus?.selfie || 'pending',
      required: true
    },
    {
      id: 'source_of_funds',
      title: 'Source of Funds',
      description: 'Employment or income verification',
      status: kycStatus?.sourceOfFunds || 'not_started',
      required: false
    }
  ];

  const overallStatus = kycStatus?.overall || 'pending';
  const completedSteps = verificationSteps?.filter(step => step?.status === 'completed')?.length;
  const totalSteps = verificationSteps?.length;
  const progressPercentage = (completedSteps / totalSteps) * 100;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">KYC Verification Status</h2>
            <p className="text-sm text-muted-foreground">Complete your verification to unlock full features</p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(overallStatus)}`}>
          <div className="flex items-center space-x-2">
            <Icon name={getStatusIcon(overallStatus)} size={16} />
            <span>{getStatusText(overallStatus)}</span>
          </div>
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Verification Progress</span>
          <span className="text-sm text-muted-foreground">{completedSteps}/{totalSteps} completed</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
      {/* Verification Steps */}
      <div className="space-y-4">
        {verificationSteps?.map((step, index) => (
          <div key={step?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${getStatusColor(step?.status)}`}>
                <Icon name={getStatusIcon(step?.status)} size={16} />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="font-medium text-foreground">{step?.title}</h3>
                  {step?.required && (
                    <span className="text-xs bg-error/10 text-error px-2 py-1 rounded">Required</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{step?.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`text-sm font-medium ${getStatusColor(step?.status)?.split(' ')?.[0]}`}>
                {getStatusText(step?.status)}
              </span>
              {step?.status === 'not_started' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/document-upload')}
                  iconName="Upload"
                  iconPosition="left"
                  iconSize={14}
                >
                  Upload
                </Button>
              )}
              {step?.status === 'rejected' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/document-upload')}
                  iconName="RefreshCw"
                  iconPosition="left"
                  iconSize={14}
                >
                  Retry
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
        <div className="text-sm text-muted-foreground">
          {overallStatus === 'completed' ? (
            <div className="flex items-center space-x-2 text-success">
              <Icon name="CheckCircle" size={16} />
              <span>Your account is fully verified</span>
            </div>
          ) : (
            <span>Complete all required steps to verify your account</span>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            iconSize={16}
          >
            Refresh Status
          </Button>
          {overallStatus !== 'completed' && (
            <Button
              onClick={() => navigate('/document-upload')}
              iconName="Upload"
              iconPosition="left"
              iconSize={16}
            >
              Complete Verification
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default KYCStatusPanel;